/**
 * ATL-1445 stage 3, P8 — which v1 dual-write requests are domain W, and how one becomes an outbox v2
 * entry (design `docs/technical/write-cutover-stage3-app-seam.md` §2 "Migrating v1 entries at the flip",
 * §4 "Backend 409 on a stray Firestore-first write", §16).
 *
 * Pure: no storage, no clock, no network. The migration (`v1Migration.ts`) and the 409 conversion
 * (`mirrorRefusalConversion.ts`) are the two callers, and they must agree on both answers, so both live
 * here once.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * W MEMBERSHIP IS DECIDED BY THE REQUEST'S ROUTE, THROUGH THE REGISTRY
 *
 * A v1 entry is in W when the backend route it replays writes a fenced books table:
 * `isWRoute(METHOD /path)` — the W-writer registry's own predicate (`wWriterRegistry.ts`), the one
 * `scripts/__tests__/wWriterRegistry.test.js` holds to the backend's fenced-table evidence (a backend
 * route that can write a fenced table and is not in it fails CI there). Never a hand list.
 *
 * Not the LABEL, deliberately. A label (`sales.create`) is telemetry: it is not in the registry, it is
 * not what the backend fences or what B1 refuses, and one module's labels span both sides —
 * `organizations.create` sends `POST /organizations` (W: signup seeds the first store, products, …) while
 * `organizations.update` sends `PATCH /organizations/:id` (not W). The route is persisted with every v1
 * entry and is exactly what a replay sends.
 *
 * A request whose route cannot be read (an absolute URL, no leading `/`) is UNCLASSIFIABLE, never "not
 * W": the migration leaves it in v1 and reports it, and the conversion does not touch it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE V2 ENTRY OF A V1 WRITE
 *
 *  - `entryId` is the v1 id (§2: "keeping entryId = the v1 id"), which is what makes a re-run after a
 *    crash skip what it already copied (the store refuses a duplicate id). v1 ids are `[a-z0-9-]` and
 *    therefore storage-safe; an id that is not (an older bundle's, a hand-edited queue) is replaced by a
 *    DETERMINISTIC derived id (`v1x-<digest>`), so the re-run rule still holds for it.
 *  - `kind` is chosen for what outbox v2 DOES with it, because a v1 entry does not say what it is:
 *      DELETE request                       → `delete` (a 404 meaning "entity absent" is delivered);
 *      absolute stock request (P3's rule)   → `absolute` (held, below);
 *      anything else                        → `delta`.
 *    `delta` is never a purge candidate (`planDeletePurge` purges only `create`/`update`), and its
 *    presence withholds the purge of the document's live entries. So a later delete can never withdraw
 *    a migrated write whose real shape the device cannot verify (`POST /sales/:id/payment` is not a
 *    create, though it is a POST): the migrated write is delivered, then the delete. That is the
 *    conservative order — the backend applies both — rather than a purge that could drop a money leg.
 *  - `relatedIds: []`, `effects: null`, `bundleId: null`: v1 recorded none of them. (The local view,
 *    P6, therefore shows nothing for a migrated entry; see §16's open questions.)
 *  - `enqueuedAt` is the v1 entry's: its age is the write's age.
 *  - `attempts: 0`, `lastError: null`, due at once — except:
 *  - an ABSOLUTE STOCK request (`isAbsoluteStockRequest`: `POST /products/stock/bulk`,
 *    `PATCH /products/:id/stock`) is stored ALREADY HELD, `lastError: { status: null, code:
 *    'absolute-replay' }`. §2: "held for an operator decision, never replayed" — re-asserting a stock
 *    level onto the ledger after the flip is the ATL-707 class in reverse. Its origin is never `live`,
 *    so even unheld the drainer would hold it on sight (P3, review 1 S4); storing it held makes the
 *    reason visible to the operator surface from the moment it is copied, not only once it reaches the
 *    head. Only `outboxOperator.resolveOperatorHold` may move it, and only to discard it.
 *  - The request is the v1 request's `method`, `url`, `data` and `params`, verbatim — exactly what a v1
 *    replay sends (`typedClient.replayRequest(entry.request)`). The store's validation still applies; an
 *    entry it refuses is not converted (reported, left in v1).
 */
import type { OutboxEntry as V1OutboxEntry } from '../Firebase/dualWriteOutbox'
import { isAbsoluteStockRequest } from './outboxClassify'
import { ABSOLUTE_REPLAY_CODE } from './outboxDrainer'
import {
  isStorageSafeId,
  OUTBOX_ENTRY_VERSION,
  OUTBOX_REQUEST_METHODS,
  prepareNewEntry,
  type NewOutboxEntry,
  type OutboxEntryOrigin,
  type OutboxRequest,
  type OutboxRequestMethod,
  type OutboxWriteKind,
} from './outboxEntry'
import { isWRoute } from './wRoutes'

/** The two origins a v1 write can arrive in v2 with (never `live`: P3's S4 precondition). */
export type V1DerivedOrigin = Extract<OutboxEntryOrigin, 'v1-migrated' | 'mirror-409-converted'>

/** A request as v1 persists it (`ReplayableRequestConfig`, read back from storage). */
export interface V1Request {
  method: string
  url: string
  data?: unknown
  params?: unknown
}

export type WClassification = 'w' | 'not-w' | 'unclassifiable'

/** `METHOD /path` (no query) of a v1 request, or null when the URL is not a path relative to the API base. */
export function v1RouteOf(request: Pick<V1Request, 'method' | 'url'>): string | null {
  if (typeof request.method !== 'string' || typeof request.url !== 'string') return null
  const path = request.url.split('?')[0]
  if (!path.startsWith('/') || path.startsWith('//')) return null
  return `${request.method.toUpperCase()} ${path}`
}

/** Is this persisted request a domain-W write? Decided by the registry (module docblock). */
export function classifyV1Request(request: Pick<V1Request, 'method' | 'url'>): WClassification {
  const route = v1RouteOf(request)
  if (route === null) return 'unclassifiable'
  return isWRoute(route) ? 'w' : 'not-w'
}

/** FNV-1a, 32 bits, from a given offset basis — for a deterministic, storage-safe derived id. */
function fnv1a32(text: string, basis: number): string {
  let hash = basis >>> 0
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16).padStart(8, '0')
}

/** The v2 `entryId` of a v1 entry: its own id when storage-safe, else a deterministic digest of it. */
export function v2EntryIdForV1(v1Id: string): string {
  // `isStorageSafeId` is a type guard over `unknown`; here only its verdict is wanted.
  const safe: boolean = isStorageSafeId(v1Id)
  if (safe) return v1Id
  return `v1x-${fnv1a32(v1Id, 0x811c9dc5)}${fnv1a32(v1Id, 0x01000193)}${v1Id.length.toString(36)}`
}

/** The v2 write kind of a v1 request (module docblock). */
export function v2KindForV1Request(request: Pick<OutboxRequest, 'method' | 'url'>): OutboxWriteKind {
  if (request.method === 'DELETE') return 'delete'
  if (isAbsoluteStockRequest(request)) return 'absolute'
  return 'delta'
}

const isMethod = (value: unknown): value is OutboxRequestMethod =>
  typeof value === 'string' && (OUTBOX_REQUEST_METHODS as readonly string[]).includes(value)

export interface V2FromV1Input {
  entryId: string
  orgFirestoreId: string
  label: string
  documentId: string
  request: V1Request
  enqueuedAt: number
  origin: V1DerivedOrigin
}

export type V2FromV1 = { ok: true; entry: NewOutboxEntry; heldAbsolute: boolean } | { ok: false; reason: string }

/**
 * The validated v2 entry for a v1 write, or why the store would refuse it. `now` stamps `heldSince` of
 * an absolute stock request (held from the moment it is copied).
 */
export function v2EntryFromV1Write(input: V2FromV1Input, now: number): V2FromV1 {
  const method = input.request.method
  if (!isMethod(method)) return { ok: false, reason: `request.method: ${String(method)} is not a write method` }
  const route = { method, url: input.request.url }
  const heldAbsolute = isAbsoluteStockRequest(route)
  // Built as plain data and handed to the store's own validator, which is what decides whether it is
  // an entry: the v1 payload was read back from storage and is not trusted to be JSON-clean.
  const candidate: Record<string, unknown> = {
    v: OUTBOX_ENTRY_VERSION,
    entryId: input.entryId,
    orgFirestoreId: input.orgFirestoreId,
    label: input.label,
    documentId: input.documentId,
    relatedIds: [],
    kind: v2KindForV1Request(route),
    // Verbatim, and only the fields that are present: v1 replays exactly these four.
    request: {
      ...route,
      ...(input.request.data !== undefined ? { data: input.request.data } : {}),
      ...(input.request.params !== undefined ? { params: input.request.params } : {}),
    },
    effects: null,
    enqueuedAt: input.enqueuedAt,
    bundleId: null,
    origin: input.origin,
    state: heldAbsolute ? 'held' : 'pending',
    attempts: 0,
    lastError: heldAbsolute ? { status: null, code: ABSOLUTE_REPLAY_CODE } : null,
    heldSince: heldAbsolute ? now : null,
    nextAttemptAt: 0,
  }
  const prepared = prepareNewEntry(candidate)
  if (!prepared.ok) return { ok: false, reason: prepared.reason }
  return { ok: true, entry: prepared.value, heldAbsolute }
}

/** `v2EntryFromV1Write` for a persisted v1 entry (the migration, and a v1 drain conversion). */
export function v2EntryFromV1Entry(entry: V1OutboxEntry, origin: V1DerivedOrigin, now: number): V2FromV1 {
  return v2EntryFromV1Write(
    {
      entryId: v2EntryIdForV1(entry.id),
      orgFirestoreId: entry.orgFirestoreId,
      label: entry.label,
      documentId: entry.firestoreId,
      request: entry.request,
      enqueuedAt: entry.enqueuedAt,
      origin,
    },
    now,
  )
}
